import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLocale } from '../i18n/LocaleContext';
import { locPath, stripLocale } from '../i18n/paths';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://skdesign.be';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const SITE_NAME = 'Sander Kuyken — Creative Designer';

const SEO = ({
  title,
  description,
  path,
  image,
  type = 'website',
  jsonLd,
  noIndex = false,
}) => {
  const location = useLocation();
  const { lang } = useLocale();
  const rawPath = path != null ? path : location.pathname;
  const isAdmin = rawPath.startsWith('/admin');
  const rest = path != null ? path : stripLocale(location.pathname);
  const localizedPath = isAdmin ? rawPath : locPath(lang, rest === '/' ? '/' : rest);
  const canonical = `${SITE_URL}${localizedPath}`;
  const enUrl = `${SITE_URL}${locPath('en', rest === '/' ? '/' : rest)}`;
  const nlUrl = `${SITE_URL}${locPath('nl', rest === '/' ? '/' : rest)}`;
  const htmlLang = lang === 'nl' ? 'nl-BE' : 'en-BE';
  const ogLocale = lang === 'nl' ? 'nl_BE' : 'en_BE';
  const ogAlternate = lang === 'nl' ? 'en_BE' : 'nl_BE';

  const fullTitle = title ? `${title} | Sander Kuyken` : SITE_NAME;
  const ogImage = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
    : DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {!isAdmin && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {!isAdmin && <link rel="alternate" hrefLang="nl" href={nlUrl} />}
      {!isAdmin && <link rel="alternate" hrefLang="x-default" href={enUrl} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogAlternate} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
